import type { LucideIcon } from 'lucide-react'
import { CreditCard, Home, PawPrint, User } from 'lucide-react'
import { Link, useMatch } from 'react-router-dom'

import snootsLogo from '@/customer/assets/snoots-logo.png'
import { cn } from '@/customer/lib/utils'
import { routes } from '@/customer/routes'

/**
 * Portal-wide navigation.
 *
 * Two presentations sharing one items array:
 * - `<PortalSidebar>` - fixed rail on the left, visible at `md` and up.
 * - `<PortalBottomBar>` - fixed strip along the bottom, visible below `md`.
 *
 * DESIGN.md compliance: no black surfaces. The rail sits on the
 * primary (yellow) brand colour. Active state is expressed via full
 * text contrast + an underline under the label (matching the
 * stacked-headline underline pattern from DESIGN.md); inactive tiles
 * dim their icon + label so the difference reads at a glance without
 * a second colour block.
 */

type NavItem = {
  path: string
  end: boolean
  label: string
  icon: LucideIcon
}

/**
 * Nav destinations. `end` on the Home item so `/account/pets` doesn't
 * also match `/account`. All paths come straight from `routes.*` -
 * the Storybook mock swaps those for story-id paths at story runtime,
 * so tiles both navigate to and highlight against the right target
 * in either environment.
 */
const NAV_ITEMS: NavItem[] = [
  { path: routes.account.index, end: true, label: 'Home', icon: Home },
  { path: routes.account.pets.index, end: false, label: 'Pets', icon: PawPrint },
  { path: routes.account.billing.index, end: false, label: 'Billing', icon: CreditCard },
  { path: routes.account.profile, end: false, label: 'Profile', icon: User },
]

export function PortalSidebar() {
  return (
    <aside
      aria-label="Primary"
      className="hidden md:fixed md:inset-y-0 md:left-0 md:z-20 md:flex md:w-24 md:flex-col md:items-center md:gap-2 md:bg-primary md:text-primary-foreground md:p-3"
    >
      <Link
        to={routes.account.index}
        aria-label="Snoots home"
        className="mb-2 mt-1 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <img src={snootsLogo} alt="" className="h-auto w-20" />
      </Link>
      {NAV_ITEMS.map((item) => (
        <NavTile key={item.path} item={item} size="sidebar" />
      ))}
    </aside>
  )
}

export function PortalBottomBar() {
  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 bg-primary p-2 text-primary-foreground md:hidden"
      // Respect iOS safe-area so tab labels don't sit behind the home indicator.
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + var(--spacing) * 2)' }}
    >
      {NAV_ITEMS.map((item) => (
        <NavTile key={item.path} item={item} size="bottom" />
      ))}
    </nav>
  )
}

/**
 * Single nav tile - shared between sidebar and bottom bar so the
 * active/inactive treatment stays perfectly in sync across
 * breakpoints. `size` picks between the larger desktop icons + full
 * padding and the tighter mobile version.
 */
function NavTile({ item, size }: { item: NavItem; size: 'sidebar' | 'bottom' }) {
  const Icon = item.icon
  const isSidebar = size === 'sidebar'
  const isActive = useMatch({ path: item.path, end: item.end }) !== null
  return (
    <Link
      to={item.path}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        // `focus:outline-none focus-visible:outline-2` keeps keyboard
        // focus visible while hiding the persistent mouse-focus
        // outline the global `* { outline-ring/50 }` base style
        // otherwise draws.
        'group flex flex-col items-center gap-1 rounded-md px-2 py-2 transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        isSidebar ? 'w-full py-3' : 'flex-1',
        isActive ? 'text-foreground' : 'text-foreground/40'
      )}
    >
      <Icon className={isSidebar ? 'size-6' : 'size-5'} strokeWidth={2.25} />
      <span className="border-b-1 border-transparent text-button-sm group-aria-[current=page]:border-foreground">
        {item.label}
      </span>
    </Link>
  )
}
